import { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, cn, Icon } from "@nexus/ui";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { parseFailureMessage, type FailureIssue } from "@/lib/failure-message";

/** Enough to see the pattern in a rejected payload without burying the page. */
const VISIBLE_ISSUE_COUNT = 4;
const COPIED_RESET_MS = 2_000;

const QUIET_ACTION_CLASS =
  "h-6 rounded-sm px-1.5 text-[11px] text-error-subtle-foreground hover:bg-error-border/40 hover:text-error-subtle-foreground";

interface FailurePanelProps {
  /** Machine code for the failure, shown as the panel's title. */
  code?: string | null;
  message: string | null;
  className?: string;
}

/**
 * Why something failed, in the shape the reason deserves. A validator failure
 * gets read out field by field rather than pasted as JSON; the raw text stays
 * reachable behind a toggle for the cases where the shape of the array is
 * itself the clue. A plain sentence gets no chrome at all — a header and a copy
 * button around six words is furniture, not help.
 *
 * Shared by the event drawer and the changelog sheet so an operator learns one
 * failure layout, not one per feature.
 */
export function FailurePanel({ code, message, className }: FailurePanelProps) {
  const { t } = useTranslation();
  const rawId = useId();
  const [isRawVisible, setIsRawVisible] = useState(false);
  const [areAllIssuesVisible, setAreAllIssuesVisible] = useState(false);
  const { copied, copy } = useCopyToClipboard({ resetMs: COPIED_RESET_MS });

  const parsed = useMemo(() => parseFailureMessage(message), [message]);

  if (!code && !message) return null;

  const issues = parsed.kind === "issues" ? parsed.issues : null;
  const hasHeader = Boolean(code) || issues !== null;
  const copyText = [code, message].filter(Boolean).join("\n");
  const shownIssues = issues && !areAllIssuesVisible ? issues.slice(0, VISIBLE_ISSUE_COUNT) : issues;
  const hiddenIssueCount = issues ? issues.length - (shownIssues?.length ?? 0) : 0;

  return (
    <section
      aria-label={t("common.failure.title", "Failure")}
      className={cn("rounded-md border border-error-border bg-error-subtle", className)}
    >
      {hasHeader ? (
        <div className="flex items-start justify-between gap-3 px-4 pt-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 pt-0.5">
            <Icon
              name="alert-circle"
              size="xs"
              colorVar="--error-subtle-foreground"
              className="shrink-0"
              aria-hidden
            />
            <span className="break-all font-mono text-[10px] uppercase tracking-wide text-error-subtle-foreground">
              {code ?? t("common.failure.title", "Failure")}
            </span>
            {issues ? (
              <span className="text-[11px] text-error-subtle-foreground/80">
                {t("common.failure.fieldCount", { count: issues.length })}
              </span>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className={cn(QUIET_ACTION_CLASS, "-mt-0.5 shrink-0")}
            onClick={() => void copy(copyText)}
          >
            <Icon
              name={copied ? "check" : "copy"}
              size="xs"
              colorVar={copied ? "--success" : "--error-subtle-foreground"}
              aria-hidden
            />
            {copied ? t("common.failure.copied", "Copied") : t("common.failure.copy", "Copy error")}
          </Button>
        </div>
      ) : null}

      {shownIssues ? (
        <ul className="mt-2 flex flex-col gap-1.5 px-4 pb-3">
          {shownIssues.map((issue, index) => (
            <IssueRow key={`${issue.path ?? "root"}-${index}`} issue={issue} />
          ))}
        </ul>
      ) : null}

      {parsed.kind === "json" ? (
        <pre
          className={cn(
            "m-0 max-h-52 overflow-auto px-4 pb-3 font-mono text-[11px] leading-[1.6] text-error-subtle-foreground",
            hasHeader ? "mt-2" : "pt-3",
          )}
        >
          {parsed.text}
        </pre>
      ) : null}

      {parsed.kind === "text" ? (
        <p
          className={cn(
            "max-h-52 overflow-auto whitespace-pre-wrap break-words px-4 pb-3 text-xs leading-5 text-error-subtle-foreground",
            hasHeader ? "mt-1.5" : "pt-3",
          )}
        >
          {parsed.text}
        </p>
      ) : null}

      {issues ? (
        <div className="flex flex-wrap items-center gap-1 px-3 pb-2">
          {hiddenIssueCount > 0 || areAllIssuesVisible ? (
            <Button
              variant="ghost"
              size="sm"
              className={QUIET_ACTION_CLASS}
              aria-expanded={areAllIssuesVisible}
              onClick={() => setAreAllIssuesVisible((current) => !current)}
            >
              {areAllIssuesVisible
                ? t("common.failure.showFewer", "Show fewer")
                : t("common.failure.showAll", "Show all {{count}}", { count: issues.length })}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            className={QUIET_ACTION_CLASS}
            aria-expanded={isRawVisible}
            aria-controls={rawId}
            onClick={() => setIsRawVisible((current) => !current)}
          >
            {isRawVisible ? t("common.failure.hideRaw", "Hide raw") : t("common.failure.showRaw", "Show raw")}
          </Button>
        </div>
      ) : null}

      {isRawVisible ? (
        <pre
          id={rawId}
          className="m-0 max-h-52 overflow-auto border-t border-error-border/60 px-4 py-3 font-mono text-[11px] leading-[1.6] text-error-subtle-foreground"
        >
          {message}
        </pre>
      ) : null}
    </section>
  );
}

function IssueRow({ issue }: { issue: FailureIssue }) {
  const { t } = useTranslation();
  const detail = formatIssueDetail(issue, t);

  return (
    <li className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] items-baseline gap-x-3">
      {issue.path ? (
        <span className="break-all font-mono text-[11px] text-error-subtle-foreground">
          {issue.path}
        </span>
      ) : null}
      <span
        className={cn("text-xs leading-5 text-error-subtle-foreground", !issue.path && "col-span-2")}
      >
        {issue.message}
        {detail ? <span className="text-error-subtle-foreground/80"> · {detail}</span> : null}
      </span>
    </li>
  );
}

function formatIssueDetail(
  issue: FailureIssue,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string,
): string | null {
  if (issue.expected && issue.received) {
    return t("common.failure.expected", "expected {{expected}}, got {{received}}", {
      expected: issue.expected,
      received: issue.received,
    });
  }
  if (issue.expected) {
    return t("common.failure.expectedOnly", "expected {{expected}}", { expected: issue.expected });
  }
  return null;
}
